import {
  getSessionAuthorizationHeaders,
} from './sessionAuth';

interface SessionResponse {
  ok: boolean;
  authenticated?: boolean;
  csrf_token?: string;
  error?: string;
}

export interface CourseActivityAttempt {
  id: string;
  activity_id: string;
  activity_version: number;
  language: string;
  title: string;
  objective: string;
  source_origin: string;
  source_cmid: number;
  source_kind: 'retzlesson' | 'forum';
  completion_mode: 'retzia_only' | 'tutor_checkpoint';
  interaction_mode: 'speaking' | 'writing' | 'mixed';
  submission_mode: 'none' | 'text' | 'text_audio';
  delivery_available: boolean;
  status: string;
  turn_count: number;
  started_at: string | null;
  completed_at: string | null;
}

export interface CourseActivityMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface CourseActivityStartResponse {
  ok: boolean;
  error?: string;
  attempt?: CourseActivityAttempt;
}

interface CourseActivityChatResponse {
  ok: boolean;
  error?: string;
  attempt?: CourseActivityAttempt;
  messages?: CourseActivityMessage[];
  assistant_message?: CourseActivityMessage;
  suggestion?: string;
}

export interface CourseActivityConversationResult {
  attempt: CourseActivityAttempt;
  messages: CourseActivityMessage[];
}

const COURSE_ACTIVITY_ATTEMPT_STORAGE_KEY =
  'retzia_course_activity_attempt_id';

let csrfTokenPromise: Promise<string> | null = null;

const isAttemptId = (
  value: string | null
): value is string =>
  typeof value === 'string' &&
  /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(
    value
  );

const readJson = async <T>(
  response: Response
): Promise<T> => {
  const text = await response.text();

  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error(
      `RETZIA recibió una respuesta inválida (HTTP ${response.status}).`
    );
  }
};

const getCsrfToken = async (): Promise<string> => {
  if (!csrfTokenPromise) {
    csrfTokenPromise = fetch(
      '/api/session.php',
      {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
        headers: {
          ...getSessionAuthorizationHeaders(),
          Accept: 'application/json',
        },
      }
    )
      .then(async response => {
        const data =
          await readJson<SessionResponse>(response);

        if (
          !response.ok ||
          !data.ok ||
          !data.authenticated ||
          !data.csrf_token
        ) {
          throw new Error(
            data.error ??
            'No fue posible comprobar la sesión.'
          );
        }

        return data.csrf_token;
      })
      .catch(error => {
        csrfTokenPromise = null;
        throw error;
      });
  }

  return csrfTokenPromise;
};

const postCourseActivityChat = async (
  attemptId: string,
  payload: Record<string, unknown>
): Promise<CourseActivityChatResponse> => {
  const csrfToken = await getCsrfToken();

  const response = await fetch(
    '/api/course-activity-chat.php',
    {
      method: 'POST',
      credentials: 'include',
      cache: 'no-store',
      headers: {
        ...getSessionAuthorizationHeaders(),
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({
        attempt_id: attemptId,
        ...payload,
      }),
    }
  );

  const data =
    await readJson<CourseActivityChatResponse>(
      response
    );

  if (!response.ok || !data.ok) {
    throw new Error(
      data.error ??
      'No fue posible continuar la actividad.'
    );
  }

  return data;
};

const requireConversation = (
  data: CourseActivityChatResponse
): CourseActivityConversationResult => {
  if (
    !data.attempt ||
    !Array.isArray(data.messages)
  ) {
    throw new Error(
      'La conversación de RETZIA está incompleta.'
    );
  }

  return {
    attempt: data.attempt,
    messages: data.messages,
  };
};

export const rememberCourseActivityAttemptId = (
  attemptId: string
): void => {
  if (!isAttemptId(attemptId)) {
    return;
  }

  try {
    window.sessionStorage.setItem(
      COURSE_ACTIVITY_ATTEMPT_STORAGE_KEY,
      attemptId
    );
  } catch {
    // No es crítico. El launch inicial sigue protegido
    // por el backend.
  }
};

export const getRememberedCourseActivityAttemptId =
  (): string | null => {
    try {
      const value =
        window.sessionStorage.getItem(
          COURSE_ACTIVITY_ATTEMPT_STORAGE_KEY
        );

      return isAttemptId(value)
        ? value
        : null;
    } catch {
      return null;
    }
  };

export const clearRememberedCourseActivityAttemptId =
  (): void => {
    try {
      window.sessionStorage.removeItem(
        COURSE_ACTIVITY_ATTEMPT_STORAGE_KEY
      );
    } catch {
      // Puede estar bloqueado en algunos iframes.
    }
  };

export const startCourseActivity = async (
  launchToken: string
): Promise<CourseActivityAttempt> => {
  const csrfToken = await getCsrfToken();

  const response = await fetch(
    '/api/course-activity-start.php',
    {
      method: 'POST',
      credentials: 'include',
      cache: 'no-store',
      headers: {
        ...getSessionAuthorizationHeaders(),
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({
        launch_token: launchToken,
      }),
    }
  );

  const data =
    await readJson<CourseActivityStartResponse>(
      response
    );

  if (
    !response.ok ||
    !data.ok ||
    !data.attempt
  ) {
    throw new Error(
      data.error ??
      'No fue posible iniciar la actividad.'
    );
  }

  return data.attempt;
};

export const startCourseActivityConversation = async (
  attemptId: string
): Promise<CourseActivityConversationResult> => {
  const data = await postCourseActivityChat(
    attemptId,
    {
      action: 'start',
    }
  );

  return requireConversation(data);
};

export const sendCourseActivityMessage = async (
  attemptId: string,
  content: string
): Promise<CourseActivityConversationResult> => {
  const data = await postCourseActivityChat(
    attemptId,
    {
      action: 'message',
      content,
    }
  );

  return requireConversation(data);
};

export const editCourseActivityLastMessage = async (
  attemptId: string,
  content: string
): Promise<CourseActivityConversationResult> => {
  const data = await postCourseActivityChat(
    attemptId,
    {
      action: 'edit_last',
      content,
    }
  );

  return requireConversation(data);
};

export const suggestCourseActivityReply = async (
  attemptId: string
): Promise<string | null> => {
  const data = await postCourseActivityChat(
    attemptId,
    {
      action: 'suggest',
    }
  );

  return typeof data.suggestion === 'string' &&
    data.suggestion.trim() !== ''
    ? data.suggestion.trim()
    : null;
};
